// The i18n runtime (#1063, #1114): one active locale for the whole app, and
// message catalogues split into namespaces that follow the code's chunks.
//
//   // src/i18n/catalogs/admin/index.ts
//   export const adminMessages = namespace('admin', en, {
//     fr: () => import('./fr'),
//     es: () => import('./es'),
//   })
//
//   // in a component
//   const t = useT(adminMessages)
//   t.shell.saveStatus.saved            // 'All changes saved'
//   t.shell.saveStatus.failed(2)        // '2 changes didn't save'
//
// English is imported statically by the namespace module, so it ships in the
// same chunk as the code that reads it: English users download nothing new.
// French and Spanish load through import() the first time a namespace is read
// in that locale. Until then useT suspends (the nearest <Suspense> shows its
// fallback), so there is never a flash of English. setLocale() preloads every
// namespace already in use before it switches, so a switch never suspends.

import { useSyncExternalStore } from 'react'
import { DEFAULT_LOCALE, intlTag, isAnyLocale, type AnyLocale, type Locale } from './locales'

/** A catalogue's shape: strings, message functions, and nested groups. */
export type MessageTree = { readonly [key: string]: string | ((...args: never[]) => string) | MessageTree }

/**
 * What another language must provide for an English catalogue: the same keys,
 * functions with the same parameters, any text. `satisfies Messages<typeof en>`
 * in fr.ts / es.ts makes a missing, extra or mistyped key a compile error.
 */
export type Messages<T> = {
  readonly [K in keyof T]: T[K] extends (...args: infer A) => string
    ? (...args: A) => string
    : T[K] extends string
      ? string
      : Messages<T[K]>
}

type Loader<M> = () => Promise<{ default: Messages<M> }>

export interface Namespace<M extends MessageTree> {
  readonly id: string
  readonly en: M
  readonly loaders: Partial<Record<Exclude<Locale, 'en'>, Loader<M>>>
}

// ── the active locale ──────────────────────────────────────────────────────

let current: AnyLocale = DEFAULT_LOCALE
let version = 0
const listeners = new Set<() => void>()
const emit = () => {
  version++
  for (const l of listeners) l()
}

export function getLocale(): AnyLocale {
  return current
}

/** The BCP 47 tag for Intl in the active locale. */
export function getIntlLocale(): string {
  return intlTag(current)
}

function subscribe(l: () => void) {
  listeners.add(l)
  return () => listeners.delete(l)
}

/** Re-renders when the locale changes or a catalogue arrives. */
export function useLocale(): AnyLocale {
  useSyncExternalStore(subscribe, () => version, () => version)
  return current
}

// ── namespaces ─────────────────────────────────────────────────────────────

const registry = new Map<string, Namespace<MessageTree>>()
const loaded = new Map<string, MessageTree>() // `${ns}:${locale}`
const pending = new Map<string, Promise<void>>()
const pseudoCache = new Map<string, MessageTree>()
// The QA pseudo-locale's transform, loaded only when it's used (never in production paths).
let pseudoize: (<M extends MessageTree>(en: M) => M) | null = null

export function namespace<M extends MessageTree>(id: string, en: M, loaders: Namespace<M>['loaders'] = {}): Namespace<M> {
  const ns: Namespace<M> = { id, en, loaders }
  registry.set(id, ns as unknown as Namespace<MessageTree>)
  return ns
}

const keyOf = (id: string, locale: Locale) => `${id}:${locale}`

/** Loads one namespace in one locale (no-op for English and when loaded). */
export function loadNamespace<M extends MessageTree>(ns: Namespace<M>, locale: AnyLocale): Promise<void> {
  if (locale === 'en' || locale === 'pseudo') return Promise.resolve()
  const key = keyOf(ns.id, locale)
  if (loaded.has(key)) return Promise.resolve()
  const inflight = pending.get(key)
  if (inflight) return inflight
  const loader = ns.loaders[locale]
  if (!loader) {
    // Not translated yet: English stands in (the parity test keeps this from shipping).
    loaded.set(key, ns.en)
    return Promise.resolve()
  }
  const p = loader()
    .then((mod) => {
      loaded.set(key, mod.default as unknown as MessageTree)
    })
    .catch(() => {
      // A failed chunk load falls back to English rather than breaking the page.
      loaded.set(key, ns.en)
    })
    .finally(() => {
      pending.delete(key)
      emit()
    })
  pending.set(key, p)
  return p
}

/** The catalogue for a locale if it's ready, else null. */
function ready<M extends MessageTree>(ns: Namespace<M>, locale: AnyLocale): M | null {
  if (locale === 'en') return ns.en
  if (locale === 'pseudo') {
    if (!pseudoize) return ns.en
    let p = pseudoCache.get(ns.id)
    if (!p) pseudoCache.set(ns.id, (p = pseudoize(ns.en)))
    return p as M
  }
  return (loaded.get(keyOf(ns.id, locale)) as M | undefined) ?? null
}

/**
 * The messages of a namespace in the active locale. Suspends while a French
 * or Spanish catalogue loads for the first time.
 */
export function useT<M extends MessageTree>(ns: Namespace<M>): M {
  const locale = useLocale()
  const m = ready(ns, locale)
  if (m) return m
  throw loadNamespace(ns, locale)
}

/** The same, outside React (toasts, announcements, pure helpers): English until loaded. */
export function messages<M extends MessageTree>(ns: Namespace<M>): M {
  return ready(ns, current) ?? ns.en
}

/**
 * Switches the whole app. Every namespace already in use is loaded first, so
 * nothing on screen suspends. Unknown values are ignored.
 */
export async function setLocale(next: unknown): Promise<void> {
  if (!isAnyLocale(next) || next === current) return
  if (next === 'pseudo' && !pseudoize) pseudoize = (await import('./pseudo')).pseudoize
  await Promise.all([...registry.values()].map((ns) => loadNamespace(ns, next)))
  current = next
  if (typeof document !== 'undefined') document.documentElement.lang = next === 'pseudo' ? 'en' : next
  emit()
}

/** Tests only: back to English with nothing loaded. */
export function resetI18nForTests(): void {
  current = DEFAULT_LOCALE
  loaded.clear()
  pending.clear()
  pseudoCache.clear()
  emit()
}

/** Every registered namespace (the parity test reads the catalogues directly). */
export function registeredNamespaces(): string[] {
  return [...registry.keys()]
}
